import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { makeJerseyTexture, makeSleeveTexture, makeBadgeTexture, makeRadialTexture, luminance } from './textures';

// ─── Skeleton dimensions (world units; the rink is 18 × 30) ──────────────────
// Standing height ≈ 1.75. Every bone hangs along its local -Z axis.
export const RIG = {
    THIGH: 0.40,
    SHIN: 0.40,
    ANKLE_H: 0.17,        // ankle joint above the floor with skate upright
    HIP_X: 0.11,
    HIP_Z: -0.04,         // hip joints relative to the pelvis origin
    SPINE_Z: 0.05,
    TORSO_L: 0.48,
    SHOULDER_X: 0.205,
    SHOULDER_Z: 0.395,
    UPPER_ARM: 0.28,
    FOREARM: 0.27,
    STICK_LEN: 1.32,
    BLADE_LEN: 0.30,
    BLADE_H: 0.07,
    WHEEL_R: 0.036,
    GOALIE_GRIP: 0.36,    // goalie holds the stick this far below the knob
};

const DOWN = new THREE.Vector3(0, 0, -1);

// Geometry that hangs from a joint: Y-axis primitive → -Z, top at the joint
function hang(geo, len) {
    geo.rotateX(Math.PI / 2);
    geo.translate(0, 0, -len / 2);
    return geo;
}

function lathe(profile, segments, depthScale = 1) {
    const geo = new THREE.LatheGeometry(profile.map(([r, z]) => new THREE.Vector2(r, z)), segments);
    geo.rotateX(Math.PI / 2);
    geo.scale(1, depthScale, 1);
    return geo;
}

// ─── PlayerFactory ────────────────────────────────────────────────────────────
// Builds articulated skaters/goalies. Geometry is shared by every player and
// materials are cached per team colour; only jersey/badge textures are per player.
export default class PlayerFactory {
    constructor() {
        this._geo = this._buildGeometries();
        this._mats = new Map();
        this._common = {
            pants:  new THREE.MeshStandardMaterial({ color: 0x14181f, roughness: 0.78 }),
            skin:   new THREE.MeshStandardMaterial({ color: 0xe0ac86, roughness: 0.7 }),
            cage:   new THREE.MeshStandardMaterial({ color: 0xd8dde4, roughness: 0.25, metalness: 0.85 }),
            boot:   new THREE.MeshStandardMaterial({ color: 0x15171c, roughness: 0.45, metalness: 0.1 }),
            chassis:new THREE.MeshStandardMaterial({ color: 0xb8c0cc, roughness: 0.22, metalness: 0.9 }),
            wheel:  new THREE.MeshStandardMaterial({ color: 0xeef2f6, roughness: 0.55 }),
            shaft:  new THREE.MeshStandardMaterial({ color: 0x1b1d22, roughness: 0.35, metalness: 0.25 }),
            tape:   new THREE.MeshStandardMaterial({ color: 0xf4f4f0, roughness: 0.85 }),
            goalieGear: new THREE.MeshStandardMaterial({ color: 0xf6f7f9, roughness: 0.6 }),
            shadow: new THREE.MeshBasicMaterial({
                color: 0x000000, transparent: true, depthWrite: false,
                alphaMap: makeRadialTexture([[0, '#fff'], [0.45, '#888'], [1, '#000']]),
                opacity: 0.55,
            }),
        };
    }

    _buildGeometries() {
        const R = RIG;
        const g = {};
        g.thigh  = hang(new THREE.CapsuleGeometry(0.088, R.THIGH - 0.1, 4, 12), R.THIGH);
        g.knee   = new THREE.SphereGeometry(0.084, 12, 10);
        g.shin   = hang(new THREE.CapsuleGeometry(0.068, R.SHIN - 0.14, 4, 12), R.SHIN);
        g.girdle = lathe([[0.15, -0.21], [0.19, -0.17], [0.215, -0.08], [0.21, 0.02], [0.185, 0.08], [0.16, 0.1]], 20, 0.82);

        // Skate (ankle frame: ankle joint at origin, floor at z=-ANKLE_H, toes +Y)
        const wz = -R.ANKLE_H + R.WHEEL_R;
        g.boot  = new RoundedBoxGeometry(0.12, 0.29, 0.13, 2, 0.04);
        g.boot.translate(0, 0.035, -0.035);
        g.cuff  = new THREE.CylinderGeometry(0.066, 0.07, 0.11, 12);
        g.cuff.rotateX(Math.PI / 2);
        g.cuff.translate(0, -0.01, 0.03);
        const plateL = new THREE.BoxGeometry(0.008, 0.31, 0.05);
        plateL.translate(-0.017, 0.035, wz + 0.012);
        const plateR = plateL.clone();
        plateR.translate(0.034, 0, 0);
        g.chassis = mergeGeometries([plateL, plateR]);
        plateL.dispose(); plateR.dispose();
        g.wheel = new THREE.CylinderGeometry(R.WHEEL_R, R.WHEEL_R, 0.024, 16);
        g.wheel.rotateZ(Math.PI / 2);   // axle along X
        g.wheelZ = wz;

        // Torso (spine frame: origin at the lower back, top at TORSO_L)
        g.torso = lathe([
            [0.165, 0.0], [0.182, 0.05], [0.192, 0.15], [0.207, 0.27], [0.218, 0.345],
            [0.208, 0.405], [0.165, 0.45], [0.09, 0.474], [0.058, 0.48],
        ], 28, 0.74);
        g.shoulderCap = new THREE.SphereGeometry(0.105, 14, 10);
        g.shoulderCap.scale(1.0, 0.95, 0.82);
        g.neck  = new THREE.CylinderGeometry(0.052, 0.058, 0.09, 10);
        g.neck.rotateX(Math.PI / 2);
        g.neck.translate(0, 0, 0.03);
        g.head  = new THREE.SphereGeometry(0.094, 16, 12);
        g.head.scale(0.92, 1.0, 1.08);
        g.helmet = new THREE.SphereGeometry(0.118, 24, 14, 0, Math.PI * 2, 0, Math.PI * 0.6);
        g.helmet.rotateX(Math.PI / 2);
        g.helmet.scale(0.94, 1.1, 1.0);
        g.helmet.translate(0, -0.012, 0.012);
        g.goalieMask = new THREE.SphereGeometry(0.125, 24, 16, 0, Math.PI * 2, 0, Math.PI * 0.8);
        g.goalieMask.rotateX(Math.PI / 2);
        g.goalieMask.scale(0.95, 1.12, 1.05);
        g.cage = this._buildCage(0.125, false);
        g.goalieCage = this._buildCage(0.135, true);

        // Arms
        g.upperArm = hang(new THREE.CapsuleGeometry(0.068, R.UPPER_ARM - 0.12, 4, 10), R.UPPER_ARM);
        g.elbow    = new THREE.SphereGeometry(0.07, 10, 8);
        g.forearm  = hang(new THREE.CapsuleGeometry(0.064, R.FOREARM - 0.12, 4, 10), R.FOREARM);
        g.glove    = new RoundedBoxGeometry(0.105, 0.125, 0.13, 2, 0.035);
        g.glove.translate(0, 0.0, -0.04);
        g.gloveCuff = new THREE.CylinderGeometry(0.068, 0.062, 0.08, 10);
        g.gloveCuff.rotateX(Math.PI / 2);
        g.gloveCuff.translate(0, 0, 0.02);
        g.blocker  = new RoundedBoxGeometry(0.05, 0.24, 0.34, 2, 0.02);
        g.blocker.translate(0.07, 0.02, -0.05);
        g.catcher  = new THREE.CylinderGeometry(0.16, 0.15, 0.07, 18);
        g.catcher.rotateZ(Math.PI / 2);
        g.catcher.translate(-0.07, 0.03, -0.06);
        g.legPad   = new RoundedBoxGeometry(0.22, 0.13, 0.64, 2, 0.05);
        g.legPad.translate(0, 0.07, -0.22);

        // Stick: shaft along -Y from the grip origin; blade along +Y from the heel
        g.shaft = new THREE.CylinderGeometry(0.017, 0.014, R.STICK_LEN, 8);
        g.shaft.translate(0, -R.STICK_LEN / 2, 0);
        g.knob = new THREE.CylinderGeometry(0.021, 0.021, 0.11, 8);
        g.knob.translate(0, -0.055, 0);
        g.blade = new RoundedBoxGeometry(0.016, R.BLADE_LEN, R.BLADE_H, 1, 0.007);
        g.blade.translate(0, R.BLADE_LEN / 2, 0);
        g.goalieShaft = new THREE.CylinderGeometry(0.018, 0.018, 1.0, 8);
        g.goalieShaft.translate(0, R.GOALIE_GRIP - 0.5, 0);
        g.goaliePaddle = new THREE.BoxGeometry(0.022, 0.075, 0.5);
        g.goaliePaddle.rotateX(Math.PI / 2);
        g.goaliePaddle.translate(0, R.GOALIE_GRIP - 1.0 - 0.25, 0);
        g.goalieBlade = new RoundedBoxGeometry(0.02, 0.38, 0.1, 1, 0.008);
        g.goalieBlade.translate(0, 0.19, 0.012);

        g.shadow = new THREE.PlaneGeometry(1.5, 1.5);
        return g;
    }

    // Wire cage merged from thin tubes; sits in front of the face (+Y)
    _buildCage(r, full) {
        const parts = [];
        const zTop = full ? 0.05 : 0.0, zBot = full ? -0.13 : -0.12;
        const span = full ? 0.95 : 0.8;
        const nV = full ? 9 : 7;
        for (let i = 0; i < nV; i++) {
            const a = -span + (2 * span * i) / (nV - 1);
            const c = new THREE.CylinderGeometry(0.0045, 0.0045, zTop - zBot, 4);
            c.rotateX(Math.PI / 2);
            c.translate(r * Math.sin(a), r * Math.cos(a), (zTop + zBot) / 2);
            parts.push(c);
        }
        const rows = full ? [-0.12, -0.07, -0.02, 0.03] : [-0.11, -0.065, -0.02];
        rows.forEach(z => {
            const pts = [];
            for (let i = 0; i <= 12; i++) {
                const a = -span - 0.05 + ((2 * span + 0.1) * i) / 12;
                pts.push(new THREE.Vector3((r + 0.004) * Math.sin(a), (r + 0.004) * Math.cos(a), z));
            }
            parts.push(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 16, 0.0055, 4, false));
        });
        // TubeGeometry is indexed; cylinders too — merge needs matching attributes
        const merged = mergeGeometries(parts.map(p => { p.deleteAttribute('uv'); return p; }));
        parts.forEach(p => p.dispose());
        return merged;
    }

    _teamMaterials(color) {
        if (this._mats.has(color)) return this._mats.get(color);
        const c = new THREE.Color(color);
        const light = luminance(color) > 0.55;
        const sleeveTex = makeSleeveTexture(color);
        const m = {
            sleeve: new THREE.MeshPhysicalMaterial({ map: sleeveTex, roughness: 0.72, sheen: 0.35, sheenColor: c.clone().lerp(new THREE.Color(0xffffff), 0.4), sheenRoughness: 0.6 }),
            plain:  new THREE.MeshPhysicalMaterial({ color: c, roughness: 0.72, sheen: 0.35, sheenColor: c.clone().lerp(new THREE.Color(0xffffff), 0.4), sheenRoughness: 0.6 }),
            helmet: new THREE.MeshPhysicalMaterial({ color: light ? 0x1b2333 : c, roughness: 0.25, clearcoat: 1.0, clearcoatRoughness: 0.08 }),
            glove:  new THREE.MeshStandardMaterial({ color: c.clone().multiplyScalar(0.85), roughness: 0.6 }),
            accent: new THREE.MeshStandardMaterial({ color: c, roughness: 0.4 }),
            textures: [sleeveTex],
        };
        this._mats.set(color, m);
        return m;
    }

    // ─── Build one player ─────────────────────────────────────────────────────
    create(player) {
        const R = RIG, G = this._geo, C = this._common;
        const isGoalie = player.role === 'goalie';
        const color = player.color || '#2266ff';
        const team = this._teamMaterials(color);
        const jerseyTex = makeJerseyTexture(color, player.label || '');
        const jerseyMat = new THREE.MeshPhysicalMaterial({
            map: jerseyTex, roughness: 0.72, sheen: 0.35, sheenRoughness: 0.6,
            sheenColor: new THREE.Color(color).lerp(new THREE.Color(0xffffff), 0.4),
        });
        const badgeTex = makeBadgeTexture(player.label || '', color);
        const badgeMat = new THREE.SpriteMaterial({ map: badgeTex, transparent: true, depthTest: false, depthWrite: false });

        const mk = (geo, mat, parent, cast = true) => {
            const m = new THREE.Mesh(geo, mat);
            m.castShadow = cast;
            m.receiveShadow = false;
            parent.add(m);
            return m;
        };

        const root   = new THREE.Group();
        const pelvis = new THREE.Group();
        root.add(pelvis);
        mk(G.girdle, C.pants, pelvis);

        // Legs
        const wheels = [];
        const buildLeg = (side) => {
            const hip = new THREE.Group();
            hip.position.set(side * R.HIP_X, 0, R.HIP_Z);
            pelvis.add(hip);
            mk(G.thigh, C.pants, hip);
            const knee = new THREE.Group();
            knee.position.z = -R.THIGH;
            hip.add(knee);
            mk(G.knee, C.pants, knee);
            mk(G.shin, C.pants, knee);
            if (isGoalie) mk(G.legPad, C.goalieGear, knee);
            const ankle = new THREE.Group();
            ankle.position.z = -R.SHIN;
            knee.add(ankle);
            mk(G.boot, C.boot, ankle);
            mk(G.cuff, isGoalie ? C.boot : team.accent, ankle);
            mk(G.chassis, C.chassis, ankle);
            for (let i = 0; i < 4; i++) {
                const w = mk(G.wheel, C.wheel, ankle);
                w.position.set(0, -0.1 + i * 0.075 + 0.035, G.wheelZ);
                wheels.push(w);
            }
            return { hip, knee, ankle };
        };
        const legL = buildLeg(-1);
        const legR = buildLeg(1);

        // Upper body
        const spine = new THREE.Group();
        spine.position.z = R.SPINE_Z;
        pelvis.add(spine);
        const torso = mk(G.torso, jerseyMat, spine);
        if (isGoalie) torso.scale.set(1.18, 1.25, 1.02);
        [-1, 1].forEach(s => {
            const cap = mk(G.shoulderCap, team.plain, spine);
            cap.position.set(s * 0.195, 0, R.SHOULDER_Z + 0.01);
            if (isGoalie) cap.scale.setScalar(1.25);
        });

        const neck = new THREE.Group();
        neck.position.z = R.TORSO_L;
        spine.add(neck);
        mk(G.neck, C.skin, neck);
        const head = new THREE.Group();
        head.position.z = 0.13;
        neck.add(head);
        mk(G.head, C.skin, head);
        if (isGoalie) {
            mk(G.goalieMask, C.goalieGear, head);
            mk(G.goalieCage, C.cage, head, false);
        } else {
            mk(G.helmet, team.helmet, head);
            mk(G.cage, C.cage, head, false);
        }

        const buildArm = (side) => {
            const shoulder = new THREE.Group();
            shoulder.position.set(side * R.SHOULDER_X, 0.0, R.SHOULDER_Z);
            spine.add(shoulder);
            mk(G.upperArm, team.plain, shoulder);
            const elbow = new THREE.Group();
            elbow.position.z = -R.UPPER_ARM;
            shoulder.add(elbow);
            mk(G.elbow, team.plain, elbow);
            mk(G.forearm, team.sleeve, elbow);
            const hand = new THREE.Group();
            hand.position.z = -R.FOREARM;
            elbow.add(hand);
            mk(G.gloveCuff, team.glove, hand);
            if (isGoalie && side > 0)      mk(G.blocker, C.goalieGear, hand);
            else if (isGoalie && side < 0) mk(G.catcher, C.goalieGear, hand);
            else                           mk(G.glove, team.glove, hand);
            return { shoulder, elbow, hand };
        };
        const armL = buildArm(-1);
        const armR = buildArm(1);

        // Stick (children of root, posed in root space each frame)
        const stickShaft = new THREE.Group();
        const stickBlade = new THREE.Group();
        root.add(stickShaft, stickBlade);
        if (isGoalie) {
            mk(G.goalieShaft, C.shaft, stickShaft);
            mk(G.goaliePaddle, C.shaft, stickShaft);
            mk(G.goalieBlade, C.shaft, stickBlade);
        } else {
            mk(G.shaft, C.shaft, stickShaft);
            mk(G.knob, C.tape, stickShaft);
            mk(G.blade, C.tape, stickBlade);
        }

        // Contact shadow + floating name badge
        const shadow = mk(G.shadow, C.shadow, root, false);
        shadow.position.z = 0.008;
        shadow.renderOrder = 1;
        const badge = new THREE.Sprite(badgeMat);
        badge.scale.set(0.78, 0.29, 1);
        badge.position.z = 1.95;
        badge.renderOrder = 10;
        root.add(badge);

        root.traverse(o => { o.frustumCulled = o.isSprite ? false : o.frustumCulled; });

        const rig = {
            root, pelvis, spine, neck, head,
            legL, legR, armL, armR,
            stickShaft, stickBlade, wheels, shadow, badge,
            isGoalie,
        };

        return {
            root, rig,
            dispose: () => {
                jerseyTex.dispose(); jerseyMat.dispose();
                badgeTex.dispose(); badgeMat.dispose();
            },
        };
    }

    dispose() {
        Object.values(this._geo).forEach(g => g && g.dispose && g.dispose());
        Object.values(this._common).forEach(m => {
            if (m.alphaMap) m.alphaMap.dispose();
            m.dispose();
        });
        this._mats.forEach(m => {
            m.textures.forEach(t => t.dispose());
            ['sleeve', 'plain', 'helmet', 'glove', 'accent'].forEach(k => m[k].dispose());
        });
        this._mats.clear();
    }
}

export { DOWN };
